import { Loader2, Mic, MicOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { MicrophoneIssue } from '@/utils/microphoneAccess';

// iPhone and iPad (iPadOS reports itself as a Mac with touch). Their Safari
// keeps site permissions behind the aA menu, not an address bar icon.
const isIOSDevice = (): boolean =>
  typeof navigator !== 'undefined' &&
  (/iP(hone|ad|od)/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

interface MicrophonePermissionDialogProps {
  /** Which microphone problem to explain; null keeps the dialog closed. */
  issue: MicrophoneIssue | null;
  /** True while a retry probe is in flight. */
  isRetrying: boolean;
  /** Label for the dismiss action ("Cancel call" for outbound, "Dismiss" for incoming). */
  cancelLabel?: string;
  onRetry: () => void;
  onCancel: () => void;
}

/**
 * Blocking explanation shown when a call can't start because the browser has
 * no microphone access. Offers concrete recovery steps and a retry that
 * resumes the pending call once access is granted.
 */
const MicrophonePermissionDialog = ({
  issue,
  isRetrying,
  cancelLabel = 'Cancel call',
  onRetry,
  onCancel,
}: MicrophonePermissionDialogProps) => {
  const isPermissionIssue = issue === 'permission-denied';
  const isIOS = isIOSDevice();

  const handleOpenChange = (open: boolean): void => {
    // Esc / X / overlay click all dismiss, but never mid-retry so a late
    // probe result can't reopen a dialog the user already closed.
    if (!open && !isRetrying) onCancel();
  };

  return (
    <Dialog open={issue !== null} onOpenChange={handleOpenChange}>
      <DialogContent className="z-[1100] max-w-md">
        <DialogHeader>
          <div className="mb-1 flex size-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            {isPermissionIssue ? <MicOff className="size-5" /> : <Mic className="size-5" />}
          </div>
          <DialogTitle>
            {isPermissionIssue ? 'Allow microphone access to make calls' : 'Microphone unavailable'}
          </DialogTitle>
          <DialogDescription>
            {isPermissionIssue
              ? 'This app needs your microphone for browser calls. This call was not placed.'
              : "Your browser couldn't start a microphone, so this call was not placed."}
          </DialogDescription>
        </DialogHeader>

        {isPermissionIssue && isIOS ? (
          <div className="space-y-3 text-sm text-foreground max-md:text-[15px]">
            <ol className="list-decimal space-y-2 pl-5">
              <li>
                Tap <span className="font-medium">aA</span> (or the page menu) in Safari&rsquo;s
                address bar, then <span className="font-medium">Website Settings</span>.
              </li>
              <li>
                Set <span className="font-medium">Microphone</span> to{' '}
                <span className="font-medium">Allow</span>, then reload the page.
              </li>
            </ol>
            <p className="text-xs text-muted-foreground max-md:text-[13px]">
              Still blocked? Open the Settings app &rarr; Apps &rarr; Safari &rarr; Microphone and
              choose <span className="font-medium">Ask</span> or{' '}
              <span className="font-medium">Allow</span>, then come back and try again.
            </p>
          </div>
        ) : isPermissionIssue ? (
          <div className="space-y-3 text-sm text-foreground">
            <ol className="list-decimal space-y-2 pl-5">
              <li>
                Click the permissions icon at the left of your browser&rsquo;s address bar (a lock
                or sliders icon).
              </li>
              <li>
                Set <span className="font-medium">Microphone</span> to{' '}
                <span className="font-medium">Allow</span>, then reload the page if your browser
                asks.
              </li>
              <li>
                In Safari: open{' '}
                <span className="font-medium">
                  Safari &rarr; Settings &rarr; Websites &rarr; Microphone
                </span>{' '}
                and allow this site.
              </li>
            </ol>
            <p className="text-xs text-muted-foreground">
              Still blocked? Make sure your computer lets your browser use the microphone (System
              Settings &rarr; Privacy &amp; Security &rarr; Microphone), then come back and try
              again.
            </p>
          </div>
        ) : (
          <ul className="list-disc space-y-2 pl-5 text-sm text-foreground">
            <li>Connect a microphone or headset.</li>
            <li>Close other apps that may be using it (Zoom, Meet, FaceTime, etc.).</li>
            <li>Then try again below.</li>
          </ul>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" disabled={isRetrying} onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button type="button" disabled={isRetrying} onClick={onRetry}>
            {isRetrying ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Checking microphone…
              </>
            ) : (
              'Try microphone again'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MicrophonePermissionDialog;
